import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred'
import {
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { Flex, message, Table } from 'antd'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import * as Yup from 'yup'

import { useDispatch } from 'react-redux'
import {
  closeModal,
  openModal,
} from '../../../../../../../../store/reducers/modal/modal.slice'
import ConfirmOrderModal from '../../../../../../../components/ConfirmOrderModal'
import InputUpload from '../../../../../../../components/InputUpload'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../../constants/errors'
import { ONLY_DATE_FORMAT } from '../../../../../../../constants/global'
import useAxios, { axiosInstance } from '../../../../../../../hooks/useAxios'
import { useRecipientPhoneRule } from '../../../../../../../hooks/useRecipientPhoneRule'
import { ILead } from '../../../../../../../types/lead'
import { PaginationResponseInterface } from '../../../../../../../types/pagination'
import { IProduct } from '../../../../../../../types/product'
import { IUserLists } from '../../../../../../../types/user'
import { resolveRecipientPhone } from '../../../../../../../utils/recipient-phone-rule.util'
import { validateMinMaxValue } from '../../../../../../../utils/validateMinMaxValue'
import { OrderFormAddProductModal } from '../../../../../../Order/Features/OrderList/components/OrderFormAddProductModal'
import UserSelectSearch from '../../../../../../../components/SelectSearch/UserSelectSearch'

interface LeadOrderDrawerProps {
  isOpen: boolean
  onClose: () => void
  onUpdateOrdersListSuccess: () => void
  lead?: ILead
}

interface IProductsForm {
  product?: IProduct
  quantity?: number
  discount?: number
  isFree?: string
}

interface IOrderForm {
  name: string
  phone: string
  address: string
  subDistrict: string
  district: string
  province: string
  zipcode: string
  isCOD: number
  products: IProductsForm[]
  orderDate: string
  paymentDate: string
  distribution: string
  paymentUrl: string
  note: string
  commissionUsername: string
  assignTelesaleBy: string
}

const getOrderFormValidationSchema = () => {
  return Yup.object({
    name: Yup.string(),
    phone: Yup.string(),
    address: Yup.string(),
    subDistrict: Yup.string(),
    district: Yup.string(),
    province: Yup.string(),
    zipcode: Yup.string(),
    orderDate: Yup.string().required('กรุณาเลือกวันที่สั่งซื้อ'),
    products: Yup.array(
      Yup.object({
        product: Yup.object().required('กรุณาเลือกสินค้า'),
        quantity: Yup.number().required('กรุณากรอกจำนวนสินค้า'),
        discount: Yup.number().min(0, 'กรุณากรอกส่วนลดให้ถูกต้อง'),
      }),
    ).min(1, 'กรุณาเลือกสินค้าอย่างน้อย 1 รายการ'),
    isCOD: Yup.number().required(),
    paymentDate: Yup.string(),
    distribution: Yup.string(),
    paymentUrl: Yup.string(),
    note: Yup.string(),
    commissionUsername: Yup.string(),
    assignTelesaleBy: Yup.string(),
  })
}

const LeadOrderDrawer = ({
  isOpen,
  onClose,
  onUpdateOrdersListSuccess,
  lead,
}: LeadOrderDrawerProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [searchUserCommission, setSearchUserCommission] = useState<string>('')
  const [searchUserTelesales, setSearchUserTelesales] = useState<string>('')
  const [searchDistributionChannel, setSearchDistributionChannel] =
    useState<string>('')
  const [isModalAddProduct, setIsModalAddProduct] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [resetAutoComplete, setResetAutoComplete] = useState<string>('')
  const dispatch = useDispatch()
  const recipientPhoneRule = useRecipientPhoneRule()

  const handleCheckCustomerWarning = async (values: IOrderForm) => {
    try {
      const responseCustomerWarning = await axiosInstance.post(
        '/blacklists/checking',
        {
          name: values.name,
          phoneNumber: values.phone,
        },
      )
      if (responseCustomerWarning.data.blacklist) {
        dispatch(
          openModal({
            title: `เตือน! ลูกค้า ${values.name} อยู่ในรายการ Blacklist`,
            content: (
              <ConfirmOrderModal
                onConfirm={async () => {
                  await formik.handleSubmit()
                  dispatch(closeModal())
                }}
              />
            ),
          }),
        )
      } else {
        await formik.handleSubmit()
      }
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }
  useEffect(() => {
    formik.setValues({
      name: lead?.customer.name ?? '',
      phone: lead?.customer.phone ?? '',
      address: lead?.customer?.address ?? '',
      subDistrict: lead?.customer?.subDistrict ?? '',
      district: lead?.customer?.district ?? '',
      province: lead?.customer?.province ?? '',
      zipcode: lead?.customer?.zipcode ?? '',
      distribution: lead?.distribution?.objectId ?? '',
      isCOD: 1,
      products: [],
      orderDate: dayjs().format(),
      paymentDate: '',
      paymentUrl: '',
      note: '',
      commissionUsername: '',
      assignTelesaleBy: '',
    })
  }, [isOpen, lead])

  const formik = useFormik<IOrderForm>({
    initialValues: {
      name: '',
      phone: '',
      address: '',
      subDistrict: '',
      district: '',
      province: '',
      zipcode: '',
      isCOD: 1,
      products: [],
      orderDate: dayjs().format(),
      distribution: '',
      paymentDate: '',
      paymentUrl: '',
      note: '',
      commissionUsername: '',
      assignTelesaleBy: '',
    } as IOrderForm,
    validationSchema: getOrderFormValidationSchema(),
    onSubmit: async (values: IOrderForm) => {
      setIsLoading(true)

      const phone = values?.phone?.trim() as string
      const name = values?.name?.trim() as string

      const { check: phoneCheck, phone: recipientPhone } =
        resolveRecipientPhone(phone, recipientPhoneRule)
      if (phoneCheck.status === 'invalid') {
        message.error(phoneCheck.message)
        setIsLoading(false)
        return
      }

      if (!name) {
        message.error('กรุณากรอกชื่อ ข้อมูลผู้ติดต่อ/ ลูกค้า')
        setIsLoading(false)
        return
      }

      if (!values.products.length) {
        message.error('กรุณาเลือกสินค้าอย่างน้อย 1 รายการ')
        setIsLoading(false)
        return
      }

      try {
        await axiosInstance.post(`/order`, {
          isCrm: true,
          name: values.name.trim() as string,
          phone: recipientPhone,
          address: {
            address: values.address.trim() as string,
            subDistrict: values.subDistrict.trim() as string,
            district: values.district.trim() as string,
            province: values.province.trim() as string,
            zipcode: `${values.zipcode}`,
          },
          isCOD: !!values.isCOD,
          products: values.products.map((product) => ({
            objectId: product.product?.objectId,
            quantity: product.quantity,
            discount: product.discount,
            free: product.isFree === 'yes',
          })),
          orderDate: values.orderDate,
          commissionUsername: values.commissionUsername,
          distribution: values.distribution,
          paymentDate: values.paymentDate,
          paymentUrl: values.paymentUrl,
          note: values.note,
          assignTelesaleBy: values.assignTelesaleBy,
        })
        formik.resetForm()
        setResetAutoComplete(new Date().getTime().toString())
        onUpdateOrdersListSuccess()
        message.success('สร้างสินค้าสำเร็จ')
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  const userCommissionList = useAxios<PaginationResponseInterface<IUserLists>>({
    method: 'GET',
    url: '/users/merchant',
    params: {
      page: 1,
      perPage: 5,
      search: searchUserCommission,
      startDate: '',
      endDate: '',
    },
  })
  const handleSearchUserCommission = async (value: string) => {
    setSearchUserCommission(value)
    await userCommissionList.mutate()
  }

  const userTelesalesList = useAxios<PaginationResponseInterface<IUserLists>>({
    method: 'GET',
    url: '/users/merchant',
    params: {
      page: 1,
      perPage: 5,
      search: searchUserTelesales,
      startDate: '',
      endDate: '',
    },
  })
  const handleSearchUserTelesales = async (value: string) => {
    setSearchUserTelesales(value)
    await userTelesalesList.mutate()
  }

  // const distributionChannels = useAxios<PaginationResponseInterface<IDistributionChannel>>({
  //   method: 'GET', url: '/distribution-channels', params: {
  //     page: 1, perPage: 5, name: searchDistributionChannel, startDate: '', endDate: '',
  //   },
  // })

  const getTotal = () => {
    return formik.values.products.reduce(
      (total: number, product: IProductsForm) =>
        total +
        (product.product?.price! * product.quantity! - product.discount! || 0),
      0,
    )
  }

  const handleUploadClick = async (event: any) => {
    const file = event.target.files[0]
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await axiosInstance.post<any>('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      if (response.status !== 201) {
        return message.error('อัพโหลดรูปภาพไม่สำเร็จ')
      }
      await formik.setFieldValue('paymentUrl', response.data.url)
      return message.success('อัพโหลดรูปภาพสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const handleDeleteCollectionByIndex = (index: number) => {
    const prevCollections = formik.values.products
    formik.setFieldValue(
      'products',
      prevCollections.filter((_, i) => i !== index),
    )
  }

  const handleAddCollection = (value: IProductsForm) => {
    const prevCollections = formik.values.products
    formik.setFieldValue('products', [...prevCollections, value])
  }

  const onChangeDiscount = (index: number, value: any) => {
    const prevCollections = formik.values.products
    prevCollections[index].discount = value
    formik.setFieldValue('products', prevCollections)
  }

  const columns = [
    {
      title: 'รายการสินค้า',
      dataIndex: 'product',
      key: 'name',
      width: 220,
      render: (product: IProduct) => {
        return (
          <Flex vertical>
            <Typography variant="subtitle1" color="text.secondary">
              {product?.name}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {product?.code}
            </Typography>
          </Flex>
        )
      },
    },
    {
      title: 'ราคาต่อชิ้น',
      dataIndex: 'product',
      key: 'price',
      width: 100,
      render: (product: IProduct) => (
        <Typography variant="subtitle2" color="text.secondary">
          {(product?.price || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      title: 'จำนวน',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 150,
      render: (quantity: number) => (
        <Typography variant="subtitle2" color="text.secondary">
          {(quantity || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      title: 'ส่วนลด',
      dataIndex: 'key',
      key: 'discount',
      width: 100,
      render: (key?: number) => (
        <TextField
          fullWidth
          label="ส่วนลด"
          name="discount"
          value={formik.values.products[key!].discount || ''}
          type={'number'}
          onKeyPress={(e) => {
            if (['+', '-', 'e', '*', '.'].includes(e.key)) {
              e.preventDefault()
            }
          }}
          onChange={(e) => {
            onChangeDiscount(
              key!,
              validateMinMaxValue(
                e.target.value,
                0,
                formik.values.products[key!].product!.price || 1,
              ) || '',
            )
          }}
          helperText={
            formik.touched.products && (formik.errors.products as string)
          }
          error={!!(formik.touched.products && formik.errors.products)}
        />
      ),
    },
    {
      title: 'ราคารวม',
      dataIndex: 'product',
      key: 'total',
      width: 100,
      render: (_: undefined, record: IProductsForm, index: number) => (
        <Typography variant="subtitle2" color="text.secondary">
          {(
            record.product!.price * record.quantity! - record.discount! || 0
          ).toLocaleString()}
        </Typography>
      ),
    },
    {
      dataIndex: 'key',
      key: 'delete',
      width: 100,
      render: (key: number) => (
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <IconButton onClick={() => handleDeleteCollectionByIndex(key)}>
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ),
    },
  ]

  return (
    <>
      <OrderFormAddProductModal
        onAdd={handleAddCollection}
        isOpen={isModalAddProduct}
        onClose={(visible) => setIsModalAddProduct(visible)}
      />
      <Drawer
        anchor={'right'}
        open={isOpen}
        keepMounted
        onClose={onClose}
        PaperProps={{
          style: {
            width: isMobile ? '80%' : '40%',
            maxWidth: isMobile ? '80%' : '40%',
          },
        }}
      >
        <Box>
          <Grid container spacing={2} sx={{ padding: 2 }}>
            <Grid item xs={12} lg={12}>
              <Typography variant={'h6'}>รายการคำสั่งซื้อใหม่</Typography>
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ReportGmailerrorredIcon />
                    </InputAdornment>
                  ),
                }}
                fullWidth
                label={`หมายเหตุ`}
                name="note"
                helperText={
                  formik.touched.note && (formik.errors.note as string)
                }
                error={!!(formik.touched.note && formik.errors.note)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.note}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <Grid
                container
                spacing={2}
                justifyContent={'space-between'}
                alignItems={'center'}
              >
                <Grid item>
                  <Typography variant={'h6'}>รายการสินค้า</Typography>
                </Grid>
                <Grid item>
                  <Button
                    startIcon={<AddIcon />}
                    color="warning"
                    size="medium"
                    variant="contained"
                    onClick={() => setIsModalAddProduct(!isModalAddProduct)}
                  >
                    เพิ่มสินค้า
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} lg={12}>
              <Table
                size={isMobile ? 'small' : 'middle'}
                columns={columns}
                dataSource={formik.values.products?.map(
                  (product: IProductsForm, index: number) => {
                    return {
                      ...product,
                      key: index,
                    }
                  },
                )}
                bordered
                pagination={{ hideOnSinglePage: true }}
              />
            </Grid>
            <Grid container justifyContent={'flex-end'} mt={2}>
              <Grid item>
                <Typography variant="subtitle2" color="text.secondary">
                  ราคารวม: {getTotal().toLocaleString()} บาท
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={12} lg={6}>
              {/* <SelectSearch<IUserLists>
                resetAutoComplete={resetAutoComplete}
                label={'ลงออเดอร์ (ค่าคอม) ให้พนักงาน xxx'}
                onSelect={(e) =>
                  formik.setFieldValue('commissionUsername', e?.value.username)
                }
                search={searchUserCommission}
                onSearch={handleSearchUserCommission}
                name="commissionUsername"
                helperText={
                  formik.touched.commissionUsername &&
                  (formik.errors.commissionUsername as string)
                }
                error={
                  !!(
                    formik.touched.commissionUsername &&
                    formik.errors.commissionUsername
                  )
                }
                onBlur={formik.handleBlur}
                options={
                  userCommissionList.data?.records
                    .filter(
                      (userCommission: IUserLists) =>
                        userCommission.isAvailable,
                    )
                    .map((userCommission: IUserLists) => ({
                      label: userCommission.name,
                      image: userCommission.image,
                      value: userCommission,
                    })) || ([] as IOptionSelect<IUserLists>[])
                }
              /> */}
              <UserSelectSearch
                label={'ลงออเดอร์ (ค่าคอม) ให้พนักงาน'}
                onSelect={(e) =>
                  formik.setFieldValue('commissionUsername', e?.value.username)
                }
                name="commissionUsername"
                helperText={
                  formik.touched.commissionUsername &&
                  (formik.errors.commissionUsername as string)
                }
                error={
                  !!(
                    formik.touched.commissionUsername &&
                    formik.errors.commissionUsername
                  )
                }
                onBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              {/* <SelectSearch<IUserLists>
                resetAutoComplete={resetAutoComplete}
                label={
                  'เลือกผู้ดูแล Telesale (กรุณาเว้นว่างไว้ หากไม่ต้องการกำหนด)'
                }
                onSelect={(e) =>
                  formik.setFieldValue('assignTelesaleBy', e?.value.username)
                }
                search={searchUserTelesales}
                onSearch={handleSearchUserTelesales}
                options={
                  userTelesalesList.data?.records
                    .filter(
                      (userTelesales: IUserLists) => userTelesales.isAvailable,
                    )
                    .map((userTelesales: IUserLists) => ({
                      label: userTelesales.name,
                      image: userTelesales.image,
                      value: userTelesales,
                    })) || ([] as IOptionSelect<IUserLists>[])
                }
              /> */}
              <UserSelectSearch
                resetAutoComplete={resetAutoComplete}
                label={
                  'เลือกผู้ดูแล Telesale (กรุณาเว้นว่างไว้ หากไม่ต้องการกำหนด)'
                }
                onSelect={(e) =>
                  formik.setFieldValue('assignTelesaleBy', e?.value.username)
                }
                name="assignTelesaleBy"
                onBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Select
                id="isCOD"
                name="isCOD"
                placeholder="การชำระเงิน"
                value={formik.values.isCOD}
                onChange={formik.handleChange}
                fullWidth
              >
                <MenuItem value={1}>เก็บเงินปลายทาง</MenuItem>
                <MenuItem value={0}>ชำระเงินผ่านธนาคาร</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} lg={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format={ONLY_DATE_FORMAT}
                  // disableFuture
                  label="วันที่สั่งซื้อ"
                  name="orderDate"
                  value={
                    formik.values.orderDate
                      ? dayjs(formik.values.orderDate)
                      : null
                  }
                  onChange={(e) => {
                    formik.setFieldValue('orderDate', e?.format())
                  }}
                  sx={{ width: '100%' }}
                  slotProps={{
                    textField: {
                      helperText:
                        formik.touched.orderDate &&
                        (formik.errors.orderDate as string),
                      error: !!(
                        formik.touched.orderDate && formik.errors.orderDate
                      ),
                      onBlur: formik.handleBlur,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            {formik.values.isCOD === 0 && (
              <>
                <Grid item xs={12} lg={6}>
                  <InputUpload
                    onRemove={() => formik.setFieldValue('paymentUrl', '')}
                    handleUploadClick={handleUploadClick}
                    imageUrl={formik.values.paymentUrl ?? ''}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format={ONLY_DATE_FORMAT}
                      // disableFuture
                      label="วันที่ชำระ"
                      name="paymentDate"
                      value={
                        formik.values.paymentDate
                          ? dayjs(formik.values.paymentDate)
                          : null
                      }
                      onChange={(e) => {
                        formik.setFieldValue('paymentDate', e?.format())
                      }}
                      sx={{ width: '100%' }}
                      slotProps={{
                        textField: {
                          helperText:
                            formik.touched.paymentDate &&
                            (formik.errors.paymentDate as string),
                          error: !!(
                            formik.touched.paymentDate &&
                            formik.errors.paymentDate
                          ),
                          onBlur: formik.handleBlur,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </>
            )}
            <Grid item xs={12} lg={12}>
              <Button
                color="primary"
                onClick={() => handleCheckCustomerWarning(formik.values)}
                size="large"
                variant="contained"
                startIcon={<AddIcon />}
                fullWidth
              >
                สร้างออเดอร์
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Drawer>
    </>
  )
}

export default LeadOrderDrawer
